import { useRef } from "react";
import { NSARouterMakerResult } from "namirasoft-account-react";
import { NSBox, NSBoxEnum, NSBoxString, NSBoxTextArea, NSColumn, NSRepeaterNSBoxSchemaVariable } from "namirasoft-site-react";
import { ContentTemplateFullRow, ContentTemplateInputRow, ContentTemplateRow, ContentTemplateType } from "namirasoft-content-template";
import { ContentTemplateEntities } from "../ContentTemplateEntities";
import { ContentTemplateProps } from "../ContentTemplateProps";
import { ContentTemplateLayout } from "../ContentTemplateLayout";
import { getContentTemplateParameterTab } from "../components/ContentTemplateParameterList";
import { BaseVariableSchema } from "namirasoft-schema";

interface ContentEditPageProps extends NSARouterMakerResult, ContentTemplateProps
{
	isEdit: boolean
}

function ContentEditPage(props: ContentEditPageProps)
{
	const NSBoxString_Name = useRef<NSBoxString>(null);
	const NSBoxEnum_Type = useRef<NSBoxEnum<ContentTemplateType>>(null);
	const NSBoxTextArea_Content = useRef<NSBoxTextArea>(null);
	const NSBoxTextArea_Description = useRef<NSBoxTextArea>(null);
	const NSRepeater_Parameter = useRef<NSRepeaterNSBoxSchemaVariable>(null);

	return (
		<ContentTemplateLayout<ContentTemplateRow, ContentTemplateInputRow>
			{...props}
			action={{
				title: "",
				description: "Create a new content template to use in your messaging. Choose each to change.",
				menus: {}
			}}
			pages={{
				entity: ContentTemplateEntities.content_template(props),
				edit: {
					...props,
					ui: {
						tab_page_edit: {
							editor: {
								getInputRow: () =>
								{
									let name: string = NSBoxString_Name.current?.getValue() ?? "";
									let type: ContentTemplateType = NSBoxEnum_Type.current?.getValueEnumOne(ContentTemplateType, ContentTemplateType.Plain) ?? ContentTemplateType.Plain;
									let content: string = NSBoxTextArea_Content.current?.getValue() ?? "";
									let description: string = NSBoxTextArea_Description.current?.getValue() ?? "";
									let parameters = NSRepeater_Parameter.current?.getValue() ?? [];
									return {
										workspace_id: "", name, type, content, description, parameters,
										content_template_category: [], content_template_field: [], content_template_tag: []
									};
								},
								setRow: (row) =>
								{
									let e = row as ContentTemplateFullRow;
									NSBoxString_Name.current?.setValue(e.name);
									NSBoxEnum_Type.current?.setValue(e.type);
									NSBoxTextArea_Content.current?.setValue(e.content);
									NSBoxTextArea_Description.current?.setValue(e.description || "");;
									NSRepeater_Parameter.current?.setValue(e.parameters?.filter(p => p) as BaseVariableSchema[]);
								},

							},
							ui: {
								extra_tabs: [
									{
										title: "Content",
										clicked: true,
										getContent: () =>
											<NSColumn>
												<NSBoxTextArea
													ref={NSBoxTextArea_Content}
													title="Content"
													info={{ link: "https://namirasoft.com/content-template/console-guide/template/#field-content" }}
													required
													rows={10} style={{ width: "100%" }} />
											</NSColumn>
									},
									{
										title: "Parameters",
										clicked: true,
										getContent: () =>
											<NSColumn>
												<NSRepeaterNSBoxSchemaVariable
													ref={NSRepeater_Parameter}
													name="parameters"
													title="Parameters"
													info={{ link: "https://namirasoft.com/content-template/console-guide/template/#field-parameter" }}
													required
													item_title="Parameter"
													style={{ width: NSBox.width.triple }}
													item={{ style: { width: "100%" } }}
												/>
											</NSColumn>
									}
								]
							}
						},
						tab_page_view: { tabs: { extra: { after_general: [getContentTemplateParameterTab()] } } }
					},
				}
			}}
		>
			<NSColumn>
				<NSBoxString
					ref={NSBoxString_Name}
					required
					title="Name"
					info={{ link: "https://namirasoft.com/content-template/console-guide/template/#field-name" }}
					style={{ width: "100%" }}
				/>
				<NSBoxEnum<ContentTemplateType>
					ref={NSBoxEnum_Type}
					required
					title="Type"
					info={{ link: "https://namirasoft.com/content-template/console-guide/template/#field-type" }}
					getEnumObject={() => ContentTemplateType}
					multiple={false}
					searchable={false}
					style={{ width: "100%" }}
				/>
				<NSBoxTextArea
					ref={NSBoxTextArea_Description}
					title="Description"
					info={{ link: "https://namirasoft.com/content-template/console-guide/template/#field-description" }}
					required={false}
					style={{ width: "100%" }}
				/>
			</NSColumn>
		</ContentTemplateLayout>
	);
}

export default ContentEditPage;